import { backgrounds } from "assets/backgrounds";
import { Button, Col, Row } from "components/primitives";
import { DashboardContext } from "contexts/DashboardContext";
import { Color, ColorBox } from "material-ui-color";
import { FC, useContext, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { getCurrentDashboardProps } from "util/getCurrentDashboard";
import { findDashboardGroupById, getValidDashboards } from "util/dashboardGroups";

import { SegmentProps } from "./ToolbarContainer";
import { ToolbarSegmentHeader } from "./ToolbarSegmentHeader";

export const BackgroundsToolbar: FC<SegmentProps> = ({ setCurrentSegment, data, onSetData }) => {
  const { data: globalData, setData: updateGlobalData } = useContext(DashboardContext);
  const activeGroup = findDashboardGroupById(
    globalData.dashboardGroups,
    globalData.activeDashboardGroupId,
  );
  const dashboardBgColor = localStorage.getItem("appBgColor");
  const [bgColor, setBgColor] = useState<Color | string>(
    dashboardBgColor ? JSON.parse(dashboardBgColor) : undefined,
  );

  const dashboards = getValidDashboards(activeGroup?.dashboards);
  const activeDashboard = getCurrentDashboardProps(globalData.activeDashboardId, dashboards);

  const [showPicker, setShowPicker] = useState(false);
  const [pickerPosition, setPickerPosition] = useState({ top: 0, left: 0 });

  const pickerRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  /**
   * Toggles the color picker and sets its position based on the button position
   */
  const handleTogglePicker = () => {
    if (buttonRef.current) {
      const { top, left } = buttonRef.current.getBoundingClientRect();
      setPickerPosition({ top: top - 100, left: left + 100 });
    }
    setShowPicker((prev) => !prev);
  };

  /**
   * Updates the background color of the active dashboard based on selection
   * @param value - The selected background color
   */
  const handleChangeBg = (value: string, hexBgColor?: string) => {
    const activeGroup = findDashboardGroupById(
      globalData.dashboardGroups,
      globalData.activeDashboardGroupId,
    );

    if (
      activeGroup &&
      getValidDashboards(activeGroup.dashboards).some(
        (dashboard) => dashboard.id === globalData.activeDashboardId,
      )
    ) {
      const updatedDashboards = activeGroup.dashboards.map((dashboard) =>
        dashboard?.id === globalData.activeDashboardId
          ? { ...dashboard, backgroundName: value }
          : dashboard,
      );

      updateGlobalData({
        dashboardGroups: globalData.dashboardGroups.map((group) =>
          group?.id === globalData.activeDashboardGroupId
            ? { ...group, dashboards: updatedDashboards }
            : group,
        ),
      });
      if (value === "CUSTOM") {
        onSetData({
          ...data,
          backgroundName: "CUSTOM",
          hexBgValue: hexBgColor,
        });
      } else {
        onSetData({ ...data, backgroundName: value });
      }
    }
  };

  // Close picker on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
        setShowPicker(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <OuterContainer>
      <ToolbarSegmentHeader header="Backgrounds" onBack={() => setCurrentSegment("settings")} />
      <InnerContainer>
        {Object.entries(backgrounds).map(([key, src]) => {
          return (
            <SettingButton
              key={key}
              ref={key === "Custom" ? buttonRef : undefined}
              highlighted={key === data.backgroundName}
              onClick={() => {
                if (key === "Custom") {
                  handleTogglePicker();
                } else {
                  handleChangeBg(key);
                }
              }}
            >
              {key === "Custom" && (
                <CustomColorBox gradientBgValue="linear-gradient(to right, red, blue)" />
              )}
              {key !== "Custom" && <SettingImg src={src} />}
              {key}
            </SettingButton>
          );
        })}
        {showPicker && (
          <ColorPickerContainer ref={pickerRef} style={pickerPosition}>
            <ColorBox
              defaultValue={bgColor}
              onChange={(value) => {
                handleChangeBg("CUSTOM", `#${value.hex}`);
                setBgColor(value);
              }}
              value={bgColor}
            />
          </ColorPickerContainer>
        )}
      </InnerContainer>
    </OuterContainer>
  );
};

const OuterContainer = styled(Col)`
  height: 100%;
  overflow: auto;
`;

const InnerContainer = styled(Row)`
  gap: 10px;
  flex-wrap: wrap;
  padding: 5px;
`;

const SettingButton = styled(Button)<{ highlighted?: boolean }>`
  display: flex;
  flex-direction: column;
  width: 47%;
  aspect-ratio: 0.85;
  justify-content: center;
  align-items: center;
  padding: 5px 10px;
  box-shadow: ${({ highlighted }) =>
    highlighted ? "0 0 8px rgba(0, 9, 43, 0.7)" : "0 0 5px rgba(0, 9, 43, 0.5)"};
  border-radius: 8px;
`;

const SettingImg = styled.img`
  width: 100%;
  border: 1px solid black;
  border-radius: 3px;
`;

const ColorPickerContainer = styled.div`
  position: absolute;
  padding: 1rem;
  background: #fff;
  box-shadow: 0 0 5px rgba(0, 9, 43, 0.5);
  z-index: 10;
`;

const CustomColorBox = styled.div<{
  hexBgValue?: string;
  gradientBgValue?: string;
}>`
  width: 100%;
  height: 37px;
  border: 1px solid black;
  border-radius: 3px;
  background: ${(props) =>
    props.gradientBgValue
      ? props.gradientBgValue
      : props.hexBgValue
        ? props.hexBgValue
        : "transparent"};
`;
